import React, { useEffect, useState } from "react"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./leaderboard.scss"

// UI components and structure
import GHDLogo from "../../assets/images/golf-holidays-direct-logo.png"
import Button from "../../components/ui/button/button"
import AgentRow from "./_rows/agent"

// Functional component to return the leaderboard page
export default function Leaderboard() {
	const [allAgents, setAllAgents] = useState([])
	const [allBookings, setAllBookings] = useState([])
	const [companyTotals, setCompanyTotals] = useState({
		today: { sales: 0, margin: 0 },
		week: { sales: 0, margin: 0 },
		month: { sales: 0, margin: 0 },
	})
	const [teamTotals, setTeamTotals] = useState({
		today: { sales: 0, margin: 0 },
		week: { sales: 0, margin: 0 },
		month: { sales: 0, margin: 0 },
	})
	const [selectedLeaderboard, setSelectedLeaderboard] = useState(null)
	const [showLeaderboardSelector, setShowLeaderboardSelector] = useState(true)

	useEffect(() => {
		// Check to see if the 'officeView' is set to 1 in the URL
		const officeView = new URLSearchParams(window.location.search).get("officeView")
		if (officeView === "1") {
			setSelectedLeaderboard("GHD-MAIN-01")
			setShowLeaderboardSelector(false)
		}
	}, [])

	// Refresh the page every 5 minutes
	useEffect(() => {
		// Refresh the page every 5 minutes
		const interval = setInterval(
			() => {
				window.location.reload()
			},
			5 * 60 * 1000,
		)

		// Clear the interval when the component unmounts
		return () => clearInterval(interval)
	}, [])

	// Store all the users and their targets
	useEffect(() => {
		// Setup a snapshot listener on the users collection
		const unsubscribe = db.collection("users").onSnapshot(async (snapshot) => {
			// Filter out only the users which have the show_for.targets field set to true
			const users = snapshot.docs.filter((doc) => doc.data().show_for?.targets)

			// Map over each user and return all their document data along with the doc ID
			const usersData = users.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}))

			// For each user, get their targets from the targets collection and add it to the user data
			const usersTargetsPromises = usersData.map(async (user) => {
				const targetDoc = await db.collection("targets").doc(user.id).get()

				return targetDoc.exists ? targetDoc.data() : {}
			})

			// Wait for all target promises to resolve
			const usersTargets = await Promise.all(usersTargetsPromises)

			// Map the targets to the users
			const usersWithTargets = usersData.map((user, index) => {
				return {
					...user,
					targets: usersTargets[index],
				}
			})

			setAllAgents(usersWithTargets)
		})

		return () => unsubscribe()
	}, [])

	// Fetch all the bookings for this month
	useEffect(() => {
		// Get the start and end of the month in milliseconds
		const rangeMonthStart = firebase.firestore.Timestamp.fromMillis(moment().startOf("month").valueOf())
		const rangeMonthEnd = firebase.firestore.Timestamp.fromMillis(moment().endOf("month").valueOf())

		// Setup a snapshot listener on the bookings collection
		const unsubscribe = db
			.collection("bookings")
			.where("booked", ">=", rangeMonthStart)
			.where("booked", "<=", rangeMonthEnd)
			.onSnapshot((snapshot) => {
				// Get an array of all the booking documents
				const bookings = snapshot.docs.map((doc) => ({
					id: doc.id,
					booked: doc.data().booked,
					agent: doc.data().agent,
					margins: doc.data().margins,
				}))

				// Loop over the bookings and calculate company totals for this day, week and month
				const todayTotals = bookings.reduce(
					(acc, booking) => {
						if (moment.unix(booking.booked?.seconds).isSame(moment(), "day")) {
							if (!booking.margins?.customer_price || !booking.margins?.profit) return acc

							acc.sales += Number.parseFloat(booking.margins.customer_price)
							acc.margin += Number.parseFloat(booking.margins.profit)
							return acc
						}
						return acc
					},
					{ sales: 0, margin: 0 },
				)

				// For weekly, check if booking is within current ISO week
				const weekTotals = bookings.reduce(
					(acc, booking) => {
						const bookingDate = moment.unix(booking.booked?.seconds)
						const startOfWeek = moment().startOf("isoWeek")
						const endOfWeek = moment().endOf("isoWeek")

						if (bookingDate.isBetween(startOfWeek, endOfWeek, "day", "[]")) {
							if (!booking.margins?.customer_price || !booking.margins?.profit) return acc

							acc.sales += Number.parseFloat(booking.margins.customer_price)
							acc.margin += Number.parseFloat(booking.margins.profit)
							return acc
						}
						return acc
					},
					{ sales: 0, margin: 0 },
				)

				// For monthly, check if booking is within current month
				const monthTotals = bookings.reduce(
					(acc, booking) => {
						const bookingDate = moment.unix(booking.booked?.seconds)
						const startOfMonth = moment().startOf("month")
						const endOfMonth = moment().endOf("month")

						if (bookingDate.isBetween(startOfMonth, endOfMonth, "day", "[]")) {
							if (!booking.margins?.customer_price || !booking.margins?.profit) return acc

							acc.sales += Number.parseFloat(booking.margins.customer_price)
							acc.margin += Number.parseFloat(booking.margins.profit)
							return acc
						}
						return acc
					},
					{ sales: 0, margin: 0 },
				)

				// Set the bookings into the state, but just the booked date, agentID and margins
				setAllBookings(bookings)
				setCompanyTotals({
					today: {
						sales: todayTotals.sales.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
						margin: todayTotals.margin.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
					},
					week: {
						sales: weekTotals.sales.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
						margin: weekTotals.margin.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
					},
					month: {
						sales: monthTotals.sales.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
						margin: monthTotals.margin.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
					},
				})
			})

		return () => unsubscribe()
	}, [])

	// When the selected leaderboard is changed, update the team totals
	useEffect(() => {
		if (!selectedLeaderboard) return

		// Get all the agent IDs for the selected leaderboard
		const agents = allAgents.filter((agent) => agent.leaderboard_id === selectedLeaderboard).map((agent) => agent.id)

		// Calculate the team totals for today
		const teamTodayTotals = allBookings
			.filter((booking) => agents.includes(booking.agent))
			.reduce(
				(acc, booking) => {
					if (moment.unix(booking.booked?.seconds).isSame(moment(), "day")) {
						if (!booking.margins?.customer_price || !booking.margins?.profit) return acc

						acc.sales += Number.parseFloat(booking.margins.customer_price)
						acc.margin += Number.parseFloat(booking.margins.profit)
						return acc
					}
					return acc
				},
				{ sales: 0, margin: 0 },
			)

		// Calculate the team totals for this week
		const teamWeekTotals = allBookings
			.filter((booking) => agents.includes(booking.agent))
			.reduce(
				(acc, booking) => {
					const bookingDate = moment.unix(booking.booked?.seconds)
					const startOfWeek = moment().startOf("isoWeek")
					const endOfWeek = moment().endOf("isoWeek")

					if (bookingDate.isBetween(startOfWeek, endOfWeek, "day", "[]")) {
						if (!booking.margins?.customer_price || !booking.margins?.profit) return acc

						acc.sales += Number.parseFloat(booking.margins.customer_price)
						acc.margin += Number.parseFloat(booking.margins.profit)
						return acc
					}
					return acc
				},
				{ sales: 0, margin: 0 },
			)

		// Calculate the team totals for this month
		const teamMonthTotals = allBookings
			.filter((booking) => agents.includes(booking.agent))
			.reduce(
				(acc, booking) => {
					const bookingDate = moment.unix(booking.booked?.seconds)
					const startOfMonth = moment().startOf("month")
					const endOfMonth = moment().endOf("month")

					if (bookingDate.isBetween(startOfMonth, endOfMonth, "day", "[]")) {
						if (!booking.margins?.customer_price || !booking.margins?.profit) return acc

						acc.sales += Number.parseFloat(booking.margins.customer_price)
						acc.margin += Number.parseFloat(booking.margins.profit)
						return acc
					}
					return acc
				},
				{ sales: 0, margin: 0 },
			)

		// Set the team totals into the state
		setTeamTotals({
			today: {
				sales: teamTodayTotals.sales.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
				margin: teamTodayTotals.margin.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
			},
			week: {
				sales: teamWeekTotals.sales.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
				margin: teamWeekTotals.margin.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
			},
			month: {
				sales: teamMonthTotals.sales.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
				margin: teamMonthTotals.margin.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0 }),
			},
		})
	}, [allAgents, selectedLeaderboard])

	// Handle leaderboard selection
	const handleLeaderboardSelect = (leaderboardID) => {
		// Set the state
		setSelectedLeaderboard(leaderboardID)
		setShowLeaderboardSelector(false)
	}

	// Show leaderboard selector modal if no leaderboard is selected
	if (showLeaderboardSelector) {
		return (
			<div className="leaderboard-background">
				<div className="leaderboard-selector-overlay">
					<div className="leaderboard-selector-modal">
						<div className="leaderboard-selector-content">
							<img
								src={GHDLogo}
								alt="Golf Holidays Direct"
								className="leaderboard-selector-logo"
							/>
							<h2>Choose Your Team Leaderboard</h2>
							<p>Select which team leaderboard you would like to view:</p>

							<div className="leaderboard-options">
								<Button
									label="GHD-MAIN-01"
									fullWidth={true}
									onClick={() => handleLeaderboardSelect("GHD-MAIN-01")}
									className="leaderboard-option"
								/>
								<Button
									label="GHD-MAIN-02"
									fullWidth={true}
									onClick={() => handleLeaderboardSelect("GHD-MAIN-02")}
									className="leaderboard-option"
								/>
								<Button
									label="GHD-MAIN-03"
									fullWidth={true}
									onClick={() => handleLeaderboardSelect("GHD-MAIN-03")}
									className="leaderboard-option"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="v2-leaderboard">
			<div className="leaderboard-header">
				<img
					src={GHDLogo}
					alt="Golf Holidays Direct"
				/>
			</div>

			{/* Agent leaderboard */}
			<table>
				<thead>
					<tr>
						<td>Agent</td>
						<td>Today sales</td>
						<td>Today margin</td>
						<td>Week sales</td>
						<td>Week margin</td>
						<td>Month sales</td>
						<td>Month margin</td>
					</tr>
				</thead>

				<tbody>
					{allAgents.length > 0 &&
						allAgents
							.sort((a, b) => {
								// Calculate monthly margin for each agent
								const startOfMonth = moment().startOf("month").unix()
								const aMonthMargin = allBookings.filter((booking) => booking.agent === a.id && booking.booked?.seconds > startOfMonth).reduce((acc, booking) => acc + (booking.margins?.profit || 0), 0)
								const bMonthMargin = allBookings.filter((booking) => booking.agent === b.id && booking.booked?.seconds > startOfMonth).reduce((acc, booking) => acc + (booking.margins?.profit || 0), 0)

								// Sort by monthly margin descending first, then by first name ascending
								if (bMonthMargin !== aMonthMargin) {
									return bMonthMargin - aMonthMargin
								}
								return a.first_name.localeCompare(b.first_name)
							})
							.map((agent) => {
								return (
									<AgentRow
										key={agent.id}
										details={agent}
										leaderboardID={selectedLeaderboard}
										bookings={allBookings.filter((booking) => booking.agent === agent.id)}
									/>
								)
							})}
				</tbody>

				{/* Team totals */}
				<tfoot>
					<tr className="leaderboard-totals">
						<td>
							<small>Team Totals</small>
						</td>
						<td>
							<p>{teamTotals.today.sales}</p>
						</td>
						<td>
							<p>{teamTotals.today.margin}</p>
						</td>
						<td>
							<p>{teamTotals.week.sales}</p>
						</td>
						<td>
							<p>{teamTotals.week.margin}</p>
						</td>
						<td>
							<p>{teamTotals.month.sales}</p>
						</td>
						<td>
							<p>{teamTotals.month.margin}</p>
						</td>
					</tr>
					<tr className="leaderboard-totals">
						<td>
							<small>Company Totals</small>
						</td>
						<td>
							<p>{companyTotals.today.sales}</p>
						</td>
						<td>
							<p>{companyTotals.today.margin}</p>
						</td>
						<td>
							<p>{companyTotals.week.sales}</p>
						</td>
						<td>
							<p>{companyTotals.week.margin}</p>
						</td>
						<td>
							<p>{companyTotals.month.sales}</p>
						</td>
						<td>
							<p>{companyTotals.month.margin}</p>
						</td>
					</tr>
				</tfoot>
			</table>
		</div>
	)
}
